import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

// Iter 31cc: timeout idle per logout automatico. L'utente esce dopo 15 minuti
// di inattività (no mouse, no tastiera, no touch). Riapri = re-login.
const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minuti
const IDLE_WARNING_MS = 14 * 60 * 1000; // toast a 14' (1' prima del logout)
const IDLE_STORAGE_KEY = "rapportini_last_activity";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = auth
  const [loading, setLoading] = useState(true);
  const idleTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const warningShownRef = useRef(false);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (data?.is_owner) localStorage.setItem("il_owner_device", "1");
    } catch {
      setUser(false);
      localStorage.removeItem("rapportini_token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.access_token) {
      localStorage.setItem("rapportini_token", data.access_token);
    }
    setUser(data.user);
    if (data.user?.is_owner) localStorage.setItem("il_owner_device", "1");
    return data.user;
  };

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("rapportini_token");
    localStorage.removeItem(IDLE_STORAGE_KEY);
    setUser(false);
  }, []);

  // Iter 31cc: idle detection — logout automatico dopo 15 min senza attività.
  // Cross-tab sync via localStorage timestamp: se in un'altra tab l'utente
  // è attivo, anche questa tab resta autenticata.
  useEffect(() => {
    if (!user || user === false) return;

    const resetTimers = () => {
      warningShownRef.current = false;
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      try {
        localStorage.setItem(IDLE_STORAGE_KEY, String(Date.now()));
      } catch (_) { /* storage non disponibile */ }
      warningTimerRef.current = setTimeout(() => {
        if (!warningShownRef.current) {
          warningShownRef.current = true;
          toast.warning(
            "Verrai disconnesso tra 1 minuto per inattività. Muovi il mouse o premi un tasto per restare connesso.",
            { duration: 60000, id: "idle-warning" }
          );
        }
      }, IDLE_WARNING_MS);
      idleTimerRef.current = setTimeout(() => {
        toast.error("Sessione scaduta per inattività. Reinserisci le credenziali.", {
          duration: 8000,
        });
        logout();
      }, IDLE_TIMEOUT_MS);
    };

    const onActivity = () => resetTimers();
    const onStorage = (e) => {
      // Altre tab dello stesso utente hanno registrato attività → reset locale
      if (e.key === IDLE_STORAGE_KEY) {
        warningShownRef.current = false;
        if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        warningTimerRef.current = setTimeout(() => {
          warningShownRef.current = true;
          toast.warning(
            "Verrai disconnesso tra 1 minuto per inattività.",
            { duration: 60000, id: "idle-warning" }
          );
        }, IDLE_WARNING_MS);
        idleTimerRef.current = setTimeout(() => {
          toast.error("Sessione scaduta per inattività.", { duration: 8000 });
          logout();
        }, IDLE_TIMEOUT_MS);
      }
    };
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel"];
    events.forEach((ev) =>
      window.addEventListener(ev, onActivity, { passive: true })
    );
    window.addEventListener("storage", onStorage);
    resetTimers();
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      window.removeEventListener("storage", onStorage);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [user, logout]);

  // Visualizzatore: reinserisce la password per sbloccare allegati/stampe (30 min)
  const unlockView = async (password) => {
    await api.post("/auth/unlock-view", { password });
    await fetchMe();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, unlockView, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
